import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useReactivationSettings } from '@/hooks/useReactivationSettings';
import { useSalesDevelopment } from '@/hooks/useSalesDevelopment';
import { Loader2, RefreshCw, Info, Users, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export function ReactivationSettings() {
  const { settings, isLoading, updateSettings, isUpdating } = useReactivationSettings();
  const { salesDevelopment: sdrs, isLoading: sdrsLoading } = useSalesDevelopment();
  
  const [formData, setFormData] = useState({
    cadence_days: 60,
    attempts_per_cycle: 3,
    days_between_attempts: 1,
    max_cycles: 6,
  });

  const [selectedSdrIds, setSelectedSdrIds] = useState<string[]>([]);
  const [sdrPopoverOpen, setSdrPopoverOpen] = useState(false);

  // Atualizar form quando settings carregarem
  useEffect(() => {
    if (settings) {
      setFormData({
        cadence_days: settings.cadence_days,
        attempts_per_cycle: settings.attempts_per_cycle,
        days_between_attempts: settings.days_between_attempts,
        max_cycles: settings.max_cycles,
      });
      setSelectedSdrIds(settings.metadata?.assigned_sdr_ids || []);
    }
  }, [settings]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateSettings({
      ...formData,
      metadata: {
        ...settings?.metadata,
        assigned_sdr_ids: selectedSdrIds,
      }
    });
  };

  const handleSelectSdr = (sdrId: string) => {
    if (selectedSdrIds.includes(sdrId)) {
      setSelectedSdrIds(selectedSdrIds.filter(id => id !== sdrId));
    } else {
      setSelectedSdrIds([...selectedSdrIds, sdrId]);
    }
  };

  const handleRemoveSdr = (sdrId: string) => {
    setSelectedSdrIds(selectedSdrIds.filter(id => id !== sdrId));
  };

  const getSelectedSdrs = () => {
    return sdrs?.filter(sdr => selectedSdrIds.includes(sdr.id)) || [];
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          Configurações de Reativação
        </CardTitle>
        <CardDescription>
          Defina os parâmetros da cadência automática de reativação de leads perdidos (Deal-Centric)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertDescription>
            Quando um Deal é marcado como LOST (para contacts tipo "prospect"), o sistema agenda automaticamente 
            uma reativação. A cada ciclo, um novo Deal é criado e distribuído via round-robin entre os SDRs selecionados abaixo.
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="cadence_days">
              Intervalo entre ciclos (dias)
            </Label>
            <Input
              id="cadence_days"
              type="number"
              min="1"
              max="365"
              value={formData.cadence_days}
              onChange={(e) => setFormData({ ...formData, cadence_days: parseInt(e.target.value) })}
              disabled={isUpdating}
            />
            <p className="text-sm text-muted-foreground">
              Quantos dias após um Deal LOST iniciar a reativação (padrão: 60 dias)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="max_cycles">
              Máximo de ciclos
            </Label>
            <Input
              id="max_cycles"
              type="number"
              min="1"
              max="12"
              value={formData.max_cycles}
              onChange={(e) => setFormData({ ...formData, max_cycles: parseInt(e.target.value) })}
              disabled={isUpdating}
            />
            <p className="text-sm text-muted-foreground">
              Após este número de ciclos sem sucesso, o contato será marcado como DEAD (padrão: 6 ciclos ≈ 1 ano)
            </p>
          </div>

          {/* SDR Selection Section */}
          <div className="pt-4 border-t space-y-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <Label>SDRs de Reativação (Round-Robin)</Label>
            </div>
            
            <p className="text-sm text-muted-foreground">
              Selecione os SDRs que receberão os Deals de reativação. A distribuição é feita em round-robin.
            </p>

            {/* Selected SDRs */}
            <div className="flex flex-wrap gap-2 min-h-[36px]">
              {getSelectedSdrs().map(sdr => (
                <Badge key={sdr.id} variant="secondary" className="flex items-center gap-1 pr-1">
                  {sdr.display_name || `${sdr.first_name} ${sdr.last_name}`}
                  <button
                    type="button"
                    onClick={() => handleRemoveSdr(sdr.id)}
                    className="ml-1 rounded-full p-0.5 hover:bg-muted"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {selectedSdrIds.length === 0 && (
                <span className="text-sm text-muted-foreground italic">
                  Nenhum SDR selecionado (usará SDR original do contato como fallback)
                </span>
              )}
            </div>

            {/* SDR Selector */}
            <Popover open={sdrPopoverOpen} onOpenChange={setSdrPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={sdrsLoading || isUpdating}
                >
                  {sdrsLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Users className="h-4 w-4 mr-2" />
                  )}
                  Selecionar SDRs
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar SDR..." />
                  <CommandList
                    onWheel={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                  >
                    <CommandEmpty>Nenhum SDR encontrado.</CommandEmpty>
                    <CommandGroup>
                      {sdrs?.map(sdr => (
                        <CommandItem
                          key={sdr.id}
                          value={sdr.display_name || `${sdr.first_name} ${sdr.last_name}`}
                          onSelect={() => handleSelectSdr(sdr.id)}
                        >
                          <div className="flex items-center gap-2 w-full">
                            <div className={`w-4 h-4 border rounded flex items-center justify-center ${
                              selectedSdrIds.includes(sdr.id) ? 'bg-primary border-primary' : 'border-input'
                            }`}>
                              {selectedSdrIds.includes(sdr.id) && (
                                <span className="text-primary-foreground text-xs">✓</span>
                              )}
                            </div>
                            <span>{sdr.display_name || `${sdr.first_name} ${sdr.last_name}`}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            <Alert variant="default" className="bg-amber-50 border-amber-200">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Se nenhum SDR for selecionado, o sistema usará o SDR do deal mais recente do contato como fallback.
              </AlertDescription>
            </Alert>
          </div>

          <div className="pt-4 border-t">
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Configurações'
              )}
            </Button>
          </div>

          {settings && (
            <p className="text-xs text-muted-foreground pt-2">
              Última atualização: {new Date(settings.updated_at).toLocaleString('pt-BR')}
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
